import type { Notification } from "@app/shared/atoms/notifications-atom";

/**
 * Destination of a League notification: the server `metadata.url` when it is
 * a `/league` path (never an absolute or foreign URL), else a safe default.
 */
export function leagueNotificationUrl(
  type: string,
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const url = metadata?.url;

  if (
    typeof url === "string" &&
    /^\/league(\?[A-Za-z0-9=&_.%-]*)?$/.test(url)
  ) {
    return url;
  }

  switch (type) {
    case "gamificationLeagueResult":
      return "/league?reveal=week";
    case "leagueMonthResult":
      return "/league?reveal=month";
    case "leagueYearResult":
      return "/league?reveal=year";
    case "leaguePassed":
    case "leagueLastDay":
      return "/league?tab=week";
    default:
      return null;
  }
}

/**
 * Resolve the in-app destination for a notification from its type + metadata.
 *
 * Returns null for read-only notifications that have no navigation target
 * (e.g. `invoiceApproved` is purely informational).
 *
 * Shared by the header bell dropdown and the "see all" notifications page so
 * both navigate identically.
 */
export function resolveNotificationLink(
  notification: Pick<Notification, "type" | "metadata">,
): string | null {
  const meta = notification.metadata || {};

  switch (notification.type) {
    case "newLesson":
      return meta.lessonId ? `/lessons/${meta.lessonId}` : null;
    case "newShort":
      return meta.shortId ? `/shorts/${meta.shortId}` : null;
    case "newOffer":
      return meta.offerId ? `/offers/${meta.offerId}` : "/offers";
    case "newQuiz":
      return meta.quizId ? `/assessments/${meta.quizId}` : "/assessments";
    case "newAnnouncement":
      return "/";
    case "quizResultReady":
      return meta.attemptId
        ? `/assessments/history/${meta.attemptId}`
        : "/assessments/history";
    case "liveSessionReminder":
      return meta.sessionId
        ? `/online-sessions/${meta.sessionId}`
        : "/online-sessions";
    case "inquiryAdminReply":
      return meta.inquiryId ? `/inquiries/${meta.inquiryId}` : "/inquiries";
    case "trainingGroundReviewDue":
    case "trainingGroundQuestionFeedbackResolved":
      return "/training-ground";
    case "certificateIssued":
      return meta.certificateId
        ? `/certificates/${meta.certificateId}`
        : "/certificates";
    case "gamificationLevelUp":
      return "/my-progress";
    case "gamificationLeagueResult":
    case "leagueMonthResult":
    case "leagueYearResult":
    case "leaguePassed":
    case "leagueLastDay":
      return leagueNotificationUrl(notification.type, meta);
    case "goldStorePresentDelivered":
    case "goldStorePresentCancelled":
      return "/gold-store?view=orders";
    case "communityContentApproved":
    case "communityContentRejected":
    case "communityCommentPublished":
    case "communityNewPost":
      if (meta.communityGroupId && meta.communityPostId) {
        return `/community-groups/${meta.communityGroupId}/posts/${meta.communityPostId}`;
      }

      return meta.communityGroupId
        ? `/community-groups/${meta.communityGroupId}`
        : "/community-groups";
    // `invoiceApproved` is intentionally read-only; no destination.
    default:
      return null;
  }
}
